import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { RevealText, FadeUp } from "@/components/site/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const services = [
    t.nav.individual, t.nav.company, t.nav.followup, t.nav.eservices, t.nav.foreign, t.nav.licensing,
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error(t.contact.form.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/service-requests`, { ...form, language: lang });
      toast.success(t.contact.form.success);
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1440px] px-6 md:px-10">
        <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-6">{t.contact.eyebrow}</div>
        <RevealText lines={[t.contact.title]} as="h1" className={`display text-white ${lang === "ur" ? "text-lg sm:text-xl md:text-2xl lg:text-3xl" : lang === "en" ? "text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]" : "text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.92]"}`} />
        <FadeUp delay={0.2}>
          <p className={`mt-8 text-white/70 max-w-2xl ${lang === "ur" ? "text-base" : "text-lg"}`}>{t.contact.body}</p>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 md:px-10 mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* FORM */}
        <FadeUp className="md:col-span-7">
          <form onSubmit={onSubmit} className="sw-card rounded-md p-8 md:p-12 space-y-6" data-testid="contact-form">
            <Field label={t.contact.form.name} testId="name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-name" className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--sw-orange)] outline-none py-3 text-white placeholder:text-white/30 transition-colors" placeholder={t.contact.form.name} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label={t.contact.form.phone} testId="phone">
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="input-phone" className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--sw-orange)] outline-none py-3 text-white placeholder:text-white/30 transition-colors" placeholder="05xxxxxxxx" />
              </Field>
              <Field label={t.contact.form.email} testId="email">
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--sw-orange)] outline-none py-3 text-white placeholder:text-white/30 transition-colors" placeholder="you@email.com" />
              </Field>
            </div>
            <Field label={t.contact.form.service} testId="service">
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger data-testid="select-service" className="w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 focus:border-[color:var(--sw-orange)] focus:ring-0 text-white h-auto py-3">
                  <SelectValue placeholder={t.contact.form.selectService} />
                </SelectTrigger>
                <SelectContent className="bg-[color:var(--sw-surface)] border-white/10 text-white">
                  {services.map((s) => (
                    <SelectItem key={s} value={s} className="focus:bg-[color:var(--sw-orange)] focus:text-black">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={t.contact.form.message} testId="message">
              <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="input-message" className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--sw-orange)] outline-none py-3 text-white placeholder:text-white/30 resize-none transition-colors" placeholder={t.contact.form.message} />
            </Field>
            <button type="submit" disabled={loading} data-testid="submit-request" className="btn-primary w-full md:w-auto px-8 py-4 rounded-full text-[12px] uppercase tracking-[0.22em] font-semibold disabled:opacity-50">
              {loading ? "..." : t.contact.form.submit}
            </button>
          </form>
        </FadeUp>

        {/* CHANNELS */}
        <FadeUp delay={0.15} className="md:col-span-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-4">— {t.brand.name}</div>
          <ContactChannel icon={<Phone size={18}/>} label={t.contact.channels.phone} value="0590960314" href="tel:+966590960314" testId="channel-phone1"/>
          <ContactChannel icon={<Phone size={18}/>} label={t.contact.channels.phone} value="0567522149" href="tel:+966567522149" testId="channel-phone2"/>
          <ContactChannel icon={<MessageCircle size={18}/>} label={t.contact.channels.whatsapp} value="0590960314" href="https://wa.me/966590960314" testId="channel-whatsapp"/>
          <ContactChannel icon={<Mail size={18}/>} label={t.contact.channels.email} value="info@smartwayinsa.com" href="mailto:info@smartwayinsa.com" testId="channel-email"/>
        </FadeUp>
      </section>
    </div>
  );
};

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={`field-${testId}`}>
    <span className="text-[11px] uppercase tracking-[0.28em] text-white/50">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const ContactChannel = ({ icon, label, value, href, testId }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testId} className="group flex items-center gap-4 border-b border-white/10 pb-4 hover:border-[color:var(--sw-orange)] transition-colors">
    <span className="text-[color:var(--sw-orange)]">{icon}</span>
    <span className="flex-1">
      <span className="block text-[11px] uppercase tracking-[0.28em] text-white/50">{label}</span>
      <span className="block display text-2xl text-white group-hover:text-[color:var(--sw-orange)] transition-colors">{value}</span>
    </span>
  </a>
);

export default Contact;
